import { find } from "@leap/leapies";

type Curve = {
    xStart: number,
    yStart: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    xEnd: number,
    yEnd: number
};

enum Ctx {
    a,
    button
}

class Context {

    /**
     *
     * @param el
     */
    public static applyContext(el: HTMLElement): Ctx {
        if (Context.isButton(el)) {
            return Ctx.button;
        }

        return Ctx.a;
    }


    /**
     *
     * @param el
     * @private
     */
    private static isButton(el: HTMLElement): boolean {
        return ['wp-block-button'].some(function (e) {
            return el.classList.contains(e);
        });
    }
}

export class Squiggly {

    constructor(selector: string = '.squiggler') {
        const squigglyLines = find<HTMLElement>(selector);
        squigglyLines.forEach((squigglyLine: HTMLElement): void => {
            const context = Context.applyContext(squigglyLine);
            if (context === Ctx.button) {
                const btn = squigglyLine.querySelector('a');
                wrapInner(btn, 'span', 'class', 'd-inline-block position-relative');
            } else {
                wrapInner(squigglyLine, 'span', 'class', 'd-inline-block position-relative');
            }
            const inner = squigglyLine.querySelectorAll("span")[0] as HTMLElement;
            const width = inner.offsetWidth;
            const height = inner.offsetHeight / 2;
            const path = this.createPath(width, height);
            const strokeWidth = 1.125;
            this.addSquigglyLine(inner, path, width, height, strokeWidth);
        });
    }


    /**
     *
     * @param squigglyLine
     * @param stringPath
     * @param width
     * @param height
     * @param strokeWidth
     */
    addSquigglyLine(squigglyLine: Element, stringPath: string, width: number, height: number, strokeWidth: number): void {
        width += strokeWidth * 2;
        height += strokeWidth * 2;

        const namespace = 'http://www.w3.org/2000/svg';
        const svg = document.createElementNS(namespace, 'svg');
        // Use instead of setAttribute, because it will get converted to lowercase. viewBox should maintain its camel-case.
        svg.setAttributeNS(null, 'viewBox', `-${strokeWidth} -${strokeWidth} ${width} ${height}`);

        const paths = document.createElementNS(namespace, 'path');
        paths.setAttributeNS(null, 'stroke-width', '1px');
        paths.setAttributeNS(null, 'd', stringPath);
        const length = paths.getTotalLength();
        paths.setAttributeNS(null, 'stroke-dasharray', length.toString() + 'px');
        paths.setAttributeNS(null, 'stroke-dashoffset', length.toString() + 'px');
        svg.appendChild(paths);

        squigglyLine.appendChild(svg);
    }


    /**
     *
     * @param width
     * @param height
     */
    createPath(width: number, height: number): string {
        const numSegments = Math.floor(Math.random() * 3) + 2;
        const hSegment = height / numSegments;
        let xMin = 0;
        let xMax = width;
        let direction = Math.random() > 0.5;
        let path = `M ${direction ? xMin : xMax} 0`;

        if (!direction) {
            [xMax, xMin] = [xMin, xMax];
        }

        for (let i = 0; i < numSegments; i++) {
            const yMin = hSegment * i;
            const yMax = hSegment * (i + 1);
            const curve = this.generateCurvePoints(xMin, yMin, xMax, yMax, direction);
            if (direction) {
                xMin = curve.x2;
                xMax = curve.x1;
            } else {
                xMin = curve.x1;
                xMax = curve.x2;
            }
            direction = !direction;
            path += this.generateCurve(curve);
        }

        return path;
    }


    /**
     *
     * @param xStart
     * @param yStart
     * @param xEnd
     * @param yEnd
     * @param direction
     */
    generateCurvePoints(xStart: number, yStart: number, xEnd: number, yEnd: number, direction: boolean): Curve {
        const xMid = (xStart + xEnd) / 2;
        let x1 = getPseudoRandomNumber(xStart, xMid);
        let x2 = getPseudoRandomNumber(xMid, xEnd);
        let y1 = getPseudoRandomNumber(yStart, yEnd);
        let y2 = getPseudoRandomNumber(yStart, yEnd);

        // console.log(x1, x2, y1, y2);

        if (!direction) {
            const tempX = x1;
            x1 = x2;
            x2 = tempX;
            const tempY = y1;
            y1 = y2;
            y2 = tempY;
        }

        return {
            xStart,
            yStart,
            x1,
            y1,
            x2,
            y2,
            xEnd,
            yEnd
        };
    }


    /**
     *
     * @param curve
     */
    generateCurve(curve: Curve): string {
        return ` C ${curve.x1} ${curve.y1} ${curve.x2} ${curve.y2} ${curve.xEnd} ${curve.yEnd}`;
    }
}


/**
 * Generate a random number between min and max.
 *
 * @param min
 * @param max
 */
function getPseudoRandomNumber(min: number, max: number): number {
    // Swap values using the fancy destructuring.
    if (min > max) {
        [max, min] = [min, max];
    }

    return Math.floor(Math.random() * (max - min + 1) + min);
}


/**
 * Wrap an element's innerHTML in a new element.
 *
 * @param parent
 * @param wrapper
 * @param attribute
 * @param attributeValue
 */
function wrapInner(parent: Element, wrapper: Element | string, attribute: string = '', attributeValue: string = ''): void {
    if (typeof wrapper === "string") {
        wrapper = document.createElement(wrapper);
    }

    const el = parent.appendChild(wrapper);
    if (attribute && attributeValue) {
        el.setAttribute(attribute, attributeValue);
    }

    while (parent.firstChild !== wrapper) {
        const child = parent.firstChild;
        if (child) {
            wrapper.appendChild(child);
        }
    }
}
